import translate from '@translate';
import React, { FC, useMemo } from 'react';
import BodyTopBlock from 'src/components/BodyTopBlock';
import { ClaimIcon, DuplicateIcon, PrintIcon, ReturnIcon } from 'src/components/SkladonIcon/SmallIcons';
import VerticalSplitIcon from '@mui/icons-material/VerticalSplit';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { OrderType } from 'src/types/order';
import CommuteIcon from '@mui/icons-material/Commute';
import { BillIcon } from 'src/components/SkladonIcon/Icons';
import { progressSteps } from './constants';
import RemindersItem from 'src/components/RemindersItem';
import { dowloadRecipientInfo } from './functions';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import { showsOrderPalletsTab } from './showsOrderPalletsTab';

export interface TopBlockPropsType {
    data: OrderType | null;
    idOrder: string | undefined;
    newOrder: boolean;
    /** Archive mode: record is read-only. */
    archive?: boolean;
    actionBusy: boolean;
    disableEdit: boolean;
    disableCreateClaim: boolean;
    disableCreateReturn: boolean;
    disableSplit: boolean;
    handleSave: () => void;
    handleCreateReturn: () => void;
    handleCreateClaim: () => void;
    handleSplit: () => void;
    handleClone: () => void;
    handleReorderShipping: () => void;
    handleCreateInvoice: () => void;
    activeTab: string;
    handleChangeTab: (value: string) => void;
    handleRemoveUnavailableProducts: () => void;
    showRemoveUnavailableProducts?: boolean;
    permissionCreate: boolean;
    permissionReorderShipping: boolean;
    permissionOrderPalletsList?: boolean;
    permissionOrderTransportsList?: boolean;
    skladonUser?: number;
}

const TopBlock: FC<TopBlockPropsType> = (props) => {
    if (props.newOrder) {
        return null;
    }

    const visibleTabs = useMemo(() => {
        return progressSteps.filter(tab => {
            if (props.archive && (tab.value === 'pallets' || tab.value === 'expeditionPackages')) {
                return false;
            }
            if (tab.value === 'pallets') {
                return showsOrderPalletsTab(props.data, !!props.permissionOrderPalletsList);
            }
            if (tab.value === 'transports') {
                return !!props.permissionOrderTransportsList;
            }
            return true;
        });
    }, [props.data, props.archive, props.permissionOrderPalletsList, props.permissionOrderTransportsList]);

    return (
        <>
            <BodyTopBlock
                tabs={visibleTabs}
                selectTab={props.activeTab}
                onChangeTab={props.handleChangeTab}
                actions={[
                    {
                        tooltip: translate('orderDetail.action.splitOrder'),
                        onClick: () => {
                            props.handleSplit();
                        },
                        icon: <CallSplitIcon />,
                        hidden: props.disableSplit,
                        variant: 'outlined',
                        disabled: props.actionBusy,
                        'data-testid': 'order-detail-split-btn',
                    },
                    {
                        tooltip: translate('orderDetail.action.cloneOrder'),
                        onClick: () => {
                            props.handleClone();
                        },
                        icon: <DuplicateIcon />,
                        hidden: props.archive || !props.permissionCreate || !!props.data?.DISABLE_SHIPPING_REORDER,
                        variant: 'outlined',
                        disabled: props.actionBusy,
                        'data-testid': 'order-detail-clone-btn',
                    },
                    {
                        tooltip: translate('orderDetail.action.reorderShipping'),
                        onClick: () => {
                            props.handleReorderShipping();
                        },
                        icon: <CommuteIcon />,
                        hidden: props.archive || !props.permissionReorderShipping || !!props.data?.DISABLE_SHIPPING_REORDER,
                        variant: 'outlined',
                        disabled: props.actionBusy,
                        'data-testid': 'order-detail-reorder-shipping-btn',
                    },
                    {
                        tooltip: translate('orderDetail.action.createInvoice'),
                        onClick: () => {
                            props.handleCreateInvoice();
                        },
                        icon: <BillIcon />,
                        hidden: props.archive,
                        variant: 'outlined',
                        disabled: props.actionBusy,
                    },
                    {
                        tooltip: translate('orderDetail.action.dowloadRecipientInfo'),
                        onClick: () => {
                            dowloadRecipientInfo(props.data);
                        },
                        icon: <ContactPageIcon />,
                        hidden: props.newOrder || !props.skladonUser,
                        variant: 'outlined',
                    },
                    {
                        actionKey: 'reminders',
                        element: !props.newOrder && !props.archive ? <RemindersItem type="order" idItem={props.idOrder} /> : null,
                    },
                    {
                        items: [
                            {
                                label: translate('orderDetail.action.removeUnavailableProducts'),
                                onClick: () => {
                                    props.handleRemoveUnavailableProducts();
                                },
                                icon: <CancelOutlinedIcon />,
                                hidden: props.archive || !props.showRemoveUnavailableProducts,
                                variant: 'text',
                            },
                            {
                                label: translate('orderDetail.action.createReturn'),
                                onClick: () => {
                                    props.handleCreateReturn();
                                },
                                icon: <ReturnIcon />,
                                hidden: props.disableCreateReturn,
                                variant: 'text',
                            },
                            {
                                label: translate('orderDetail.action.createClaim'),
                                onClick: () => {
                                    props.handleCreateClaim();
                                },
                                icon: <ClaimIcon />,
                                hidden: props.disableCreateClaim,
                                variant: 'text',
                            },
                        ],
                    },
                    {
                        label: translate('orderDetail.action.save'),
                        onClick: () => {
                            props.handleSave();
                        },
                        disabled: props.actionBusy,
                        hidden: props.archive,
                        'data-testid': 'order-detail-save-btn',
                        // hidden: props.activeTab !== 'detail' || props.disableEdit,
                        // hidden: props.disableEdit,
                    },
                ]}
            />
        </>
    );
};

export default TopBlock;
